import React, { useEffect, useRef, useState } from 'react'
import { Link, usePage } from '@inertiajs/inertia-react'
import { Inertia } from '@inertiajs/inertia'
import Layout from '../../../Shared/Layout/LayoutAdmin'
import LayoutSiaLearner from '../../../Shared/Layout/LayoutSiaLearner'
import { SIA_GOLD_ACTIVE_BG, SIA_GOLD_ACTIVE_TEXT, SIA_NAVY } from '../../../constants/siaTheme'

function conditionsText(seconds) {
    return `Si cierras esta ventana o sales a otra sección, el examen se marca fallido y la calificación queda en 0. Recargar la página no lo cancela ni reinicia el tiempo de la pregunta. Solo puedes presentarlo una vez. Para aprobar necesitas 80 o más. Cada pregunta tiene ${seconds} segundos. Si el tiempo se acaba y no elegiste respuesta, esa pregunta queda fallida, sin respuesta.`
}

function seqKey(slug) {
    return `sia-exam-seq-${slug}`
}

function readSeq(slug) {
    const key = seqKey(slug)
    let seq = window.sessionStorage.getItem(key)
    if (!seq) {
        seq = `${Date.now()}-${Math.random().toString(16).slice(2)}`
        window.sessionStorage.setItem(key, seq)
    }
    return seq
}

function beacon(slug, reason) {
    const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
    const body = new FormData()
    body.append('_token', token)
    body.append('reason', reason)
    body.append('seq', readSeq(slug))
    navigator.sendBeacon(route('sia.exam.abandon', slug), body)
}

const Show = ({ mode, section, exam, lock_reason: lockReason, question, result }) => {
    const { sia_use_learner_layout: learnerLayout, errors, flash } = usePage().props
    const [selected, setSelected] = useState(question?.selected_option_id || null)
    const [secondsLeft, setSecondsLeft] = useState(question?.seconds_left ?? exam?.seconds_per_question ?? 15)
    const [localError, setLocalError] = useState('')
    const selectedRef = useRef(selected)
    const expiring = useRef(false)
    const seenIndex = useRef(null)
    selectedRef.current = selected
    if (seenIndex.current !== question?.index) {
        seenIndex.current = question?.index
        expiring.current = false
    }

    useEffect(() => {
        setSelected(question?.selected_option_id || null)
    }, [question?.index, question?.selected_option_id])

    useEffect(() => {
        if (mode !== 'wizard' || !question) return undefined
        let left = Number(question.seconds_left ?? 0)
        let stopped = false
        setSecondsLeft(left)

        const expire = () => {
            if (stopped || expiring.current) return
            expiring.current = true
            const action = question.index + 1 >= question.total ? 'finish' : 'next'
            Inertia.post(route('sia.exam.answer', section.slug), {
                action,
                option_id: selectedRef.current || null,
                timed_out: true,
            }, {
                preserveScroll: true,
                onError: () => {
                    expiring.current = false
                },
            })
        }

        if (left <= 0) {
            expire()
            return undefined
        }

        const id = window.setInterval(() => {
            left -= 1
            setSecondsLeft(Math.max(left, 0))
            if (left <= 0) {
                window.clearInterval(id)
                expire()
            }
        }, 1000)

        return () => {
            stopped = true
            window.clearInterval(id)
        }
    }, [mode, section.slug, question?.index, question?.seconds_left, question?.total])

    useEffect(() => {
        const nav = performance.getEntriesByType('navigation')[0]
        const seq = window.sessionStorage.getItem(seqKey(section.slug))
        if (nav?.type === 'reload' && seq && (mode === 'wizard' || mode === 'result')) {
            window.axios.post(route('sia.exam.resume', section.slug), { seq }).then(({ data }) => {
                if (data?.resumed && mode === 'result') {
                    Inertia.reload()
                    return
                }
                if (!data?.resumed && mode === 'result') {
                    window.sessionStorage.removeItem(seqKey(section.slug))
                }
            }).catch(() => {})
        } else if (mode === 'result') {
            window.sessionStorage.removeItem(seqKey(section.slug))
        }
    }, [mode, section.slug])

    useEffect(() => {
        if (mode !== 'wizard') return undefined

        const onLeave = (event) => {
            const visit = event.detail?.visit
            const href = String(visit?.url?.href || visit?.url || '')
            if (href.includes(`/sia/secciones/${section.slug}/examen`)) return
            beacon(section.slug, 'navigation')
            window.sessionStorage.removeItem(seqKey(section.slug))
        }

        const onHide = (event) => {
            if (event.persisted) return
            beacon(section.slug, 'close')
        }

        document.addEventListener('inertia:before', onLeave)
        window.addEventListener('pagehide', onHide)
        return () => {
            document.removeEventListener('inertia:before', onLeave)
            window.removeEventListener('pagehide', onHide)
        }
    }, [mode, section.slug])

    const fieldError = errors?.option_id || errors?.exam || errors?.action || localError
    const index = question?.index || 0

    const send = (action) => {
        if ((action === 'next' || action === 'finish') && !selected) {
            setLocalError('Elige una respuesta para continuar.')
            return
        }
        setLocalError('')
        Inertia.post(route('sia.exam.answer', section.slug), {
            action,
            option_id: selected || null,
        }, { preserveScroll: true })
    }

    return (
        <div className={'d-flex justify-content-center px-2 ' + (learnerLayout ? 'pb-10 pt-2' : 'pb-10 mt-5')}>
            <div className="w-100 rounded-3 shadow-sm bg-white overflow-hidden" style={{ maxWidth: '760px', border: '1px solid #e8e8e8' }}>
                <div className="text-center text-white py-5 px-4" style={{ backgroundColor: SIA_NAVY }}>
                    <div className="text-uppercase fw-semibold opacity-75 mb-1" style={{ letterSpacing: '0.08em' }}>
                        {section.title}
                    </div>
                    <div className="fw-bold fs-3">{exam.title}</div>
                </div>

                <div className="px-6 px-md-10 py-8">
                    {flash?.error && <div className="alert alert-danger">{flash.error}</div>}
                    {flash?.success && <div className="alert alert-success">{flash.success}</div>}

                    {mode === 'locked' && (
                        <>
                            <p className="text-gray-700 fs-5 mb-6">{lockReason}</p>
                            <Link href={route('sia.section.show', section.slug)} className="btn btn-light">
                                Volver al contenido
                            </Link>
                        </>
                    )}

                    {mode === 'intro' && (
                        <>
                            {exam.introduction && (
                                <p className="text-gray-800 fs-5 lh-lg mb-6" style={{ whiteSpace: 'pre-wrap' }}>
                                    {exam.introduction}
                                </p>
                            )}
                            <div className="rounded-3 p-5 mb-6" style={{ backgroundColor: '#fbf6e8', border: '1px solid #e6d48a' }}>
                                <div className="fw-semibold mb-2" style={{ color: '#8a7014' }}>Antes de empezar</div>
                                <p className="mb-0 text-gray-800">{conditionsText(exam.seconds_per_question || 15)}</p>
                            </div>
                            <p className="text-gray-600 mb-6">{exam.question_count} preguntas · {exam.seconds_per_question || 15} segundos por pregunta</p>
                            {fieldError && <div className="text-danger mb-4">{fieldError}</div>}
                            <div className="d-flex flex-wrap gap-3">
                                <button
                                    type="button"
                                    className="btn fw-semibold px-6"
                                    style={{ backgroundColor: SIA_GOLD_ACTIVE_BG, color: SIA_GOLD_ACTIVE_TEXT, border: 'none' }}
                                    onClick={() => Inertia.post(route('sia.exam.start', section.slug))}
                                >
                                    Comenzar examen
                                </button>
                                <Link href={route('sia.section.show', section.slug)} className="btn btn-light">
                                    Volver
                                </Link>
                            </div>
                        </>
                    )}

                    {mode === 'wizard' && question && (
                        <>
                            <div className="d-flex justify-content-between align-items-center text-gray-700 fw-semibold mb-2 gap-3">
                                <span>Pregunta {index + 1} de {question.total}</span>
                                <span style={{ color: secondsLeft <= 5 ? '#9b2c2c' : '#8a7014' }}>{secondsLeft} s</span>
                                <span>Contestadas {question.answered} · Faltan {question.remaining}</span>
                            </div>
                            <div className="rounded-pill mb-6 overflow-hidden" style={{ height: 8, backgroundColor: '#eef1f5' }}>
                                <div style={{ width: `${Math.max(((index) / question.total) * 100, 6)}%`, height: '100%', backgroundColor: SIA_GOLD_ACTIVE_BG }} />
                            </div>
                            <h2 className="fs-4 fw-semibold text-gray-900 mb-6">{question.prompt}</h2>
                            <div className="d-flex flex-column gap-3 mb-6">
                                {question.options.map((option) => {
                                    const active = Number(selected) === Number(option.id)
                                    return (
                                        <button
                                            key={option.id}
                                            type="button"
                                            className="text-start rounded-3 px-4 py-4 fw-semibold"
                                            style={{
                                                border: active ? '2px solid #c9a227' : '1px solid #e4e6ef',
                                                backgroundColor: active ? '#fbf6e8' : '#fff',
                                                color: SIA_NAVY,
                                            }}
                                            disabled={secondsLeft <= 0}
                                            onClick={() => {
                                                setSelected(option.id)
                                                setLocalError('')
                                            }}
                                        >
                                            {option.label}
                                        </button>
                                    )
                                })}
                            </div>
                            {fieldError && <div className="text-danger mb-4">{fieldError}</div>}
                            <div className="d-flex justify-content-between gap-3">
                                <button
                                    type="button"
                                    className="btn btn-light"
                                    disabled={index === 0 || secondsLeft <= 0}
                                    onClick={() => send('prev')}
                                >
                                    Anterior
                                </button>
                                {index + 1 < question.total ? (
                                    <button
                                        type="button"
                                        className="btn fw-semibold px-6"
                                        style={{ backgroundColor: SIA_GOLD_ACTIVE_BG, color: SIA_GOLD_ACTIVE_TEXT, border: 'none' }}
                                        disabled={secondsLeft <= 0}
                                        onClick={() => send('next')}
                                    >
                                        Siguiente
                                    </button>
                                ) : (
                                    <button
                                        type="button"
                                        className="btn fw-semibold px-6"
                                        style={{ backgroundColor: SIA_NAVY, color: '#fff', border: 'none' }}
                                        disabled={secondsLeft <= 0}
                                        onClick={() => send('finish')}
                                    >
                                        Finalizar
                                    </button>
                                )}
                            </div>
                        </>
                    )}

                    {mode === 'result' && result && (
                        <div className="text-center">
                            <div className="text-uppercase fw-semibold mb-2" style={{ color: result.passed ? '#0f7a3a' : '#9b2c2c', letterSpacing: '0.08em' }}>
                                {result.abandoned ? 'Examen incompleto' : result.passed ? 'Aprobado' : 'No aprobado'}
                            </div>
                            <div className="fw-bold mb-2" style={{ fontSize: '4rem', lineHeight: 1, color: SIA_NAVY }}>
                                {result.score}
                            </div>
                            <p className="text-gray-600 mb-6">de 100 · mínimo para aprobar {result.passing_score}</p>
                            {result.abandoned ? (
                                <p className="text-gray-800 fs-5 mb-6">
                                    Saliste del examen antes de terminarlo. La calificación quedó en 0. Las respuestas que alcanzaste a dar quedaron guardadas.
                                </p>
                            ) : (
                                <p className="text-gray-800 fs-5 mb-6">
                                    {result.correct_count} de {result.question_count} respuestas correctas.
                                </p>
                            )}
                            {result.closing_text && (
                                <p className="text-gray-700 mb-6" style={{ whiteSpace: 'pre-wrap' }}>{result.closing_text}</p>
                            )}
                            <p className="text-gray-600 mb-6">Este intento ya quedó registrado. No se puede volver a contestar, salvo que un administrador lo habilite.</p>
                            <Link href={route('sia.grades.index')} className="btn fw-semibold px-6" style={{ backgroundColor: SIA_GOLD_ACTIVE_BG, color: SIA_GOLD_ACTIVE_TEXT, border: 'none' }}>
                                Ver calificación final
                            </Link>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

Show.layout = (page) => {
    const title = page.props.exam?.title || 'Examen'
    if (page.props.sia_use_learner_layout) {
        return <LayoutSiaLearner title={`${title} | SIA`}>{page}</LayoutSiaLearner>
    }
    return <Layout title={title}>{page}</Layout>
}

export default Show
